import { Text } from '@thanx/uikit/text'
import type { PilotPerformance } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'

const t = buildTranslate('recovery_ai.settings.active_locations')

const ICON_TILE_SIZE = '40px'

const toPercent = (rate: number): string => `${Math.round(rate * 100)}%`

export interface PilotPerformanceBannerProps {
  /**
   * Real per-merchant figures. Present-and-outperforming → populated numbers;
   * undefined (no data yet) or not-outperforming → renders nothing. The callout
   * only celebrates a genuine win, never invented figures. The pane still gates
   * this whole component behind the dev-only flag + subset mode.
   */
  performance?: PilotPerformance
}

/**
 * AI-901 — the green pilot-vs-staff performance callout at the top of the Active
 * Locations pane. Presentational; the pane owns flag + subset gating and passes
 * the controller's `pilotPerformance`. Renders nothing without real
 * outperforming figures, or if the pilot return rate doesn't actually beat
 * staff (the copy's "just X%" asserts it) — no invented or contradictory
 * numbers. role="status" (polite) — the text carries all the meaning, so the
 * chart icon is decorative.
 */
const PilotPerformanceBanner = ({
  performance,
}: PilotPerformanceBannerProps) => {
  const [css] = useStyletron()

  // Only render for a genuine win backed by real data. No figures yet (backend
  // not supplying them) or a non-winning set → nothing, never invented numbers.
  if (!performance || !performance.isOutperformingStaff) return null

  // The copy hardcodes the direction — "…compared to just X% at staff-managed
  // locations" asserts pilot > staff. isOutperformingStaff is computed cross-repo
  // (calculate_performance_stats.rb) and could one day derive from a blended
  // score; guard the one comparison the sentence literally makes so we never
  // render "31% … compared to just 44%" as a celebration.
  if (performance.pilotReturnRate30d <= performance.staffReturnRate30d) {
    return null
  }

  const values = performance
  const responseTime = t(
    values.pilotResponseTimeMinutes === 1
      ? 'performance_banner_minutes_one'
      : 'performance_banner_minutes_other',
    { count: values.pilotResponseTimeMinutes }
  )
  const message = t(
    values.pilotLocationsCount === 1
      ? 'performance_banner_one'
      : 'performance_banner_other',
    {
      count: values.pilotLocationsCount,
      responseRate: toPercent(values.pilotResponseRate),
      responseTime,
      pilotReturn: toPercent(values.pilotReturnRate30d),
      staffReturn: toPercent(values.staffReturnRate30d),
    }
  )

  return (
    <div
      role="status"
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px',
        borderRadius: '8px',
        borderLeft: `4px solid ${palette.spearmint50}`,
        backgroundColor: palette.spearmintTint,
      })}
    >
      <div
        className={css({
          flexShrink: 0,
          width: ICON_TILE_SIZE,
          height: ICON_TILE_SIZE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          backgroundColor: palette.white,
          color: palette.spearmint50,
        })}
      >
        <i className="fa fa-line-chart" aria-hidden="true" />
      </div>
      <Text.BodyText3 color="grey90">{message}</Text.BodyText3>
    </div>
  )
}

export default PilotPerformanceBanner
